"use client"

import * as React from "react"

/**
 * The fixed top-end control cluster — ONE bar for the tenant app
 * (<HeaderControls>: language, Ask AI, Theme) and the super-admin area
 * (<AdminHeaderControls>: language, Theme), so both sit in exactly the same place.
 *
 * It floats over the sticky PageShell header, so it publishes its footprint as
 * `--header-controls-inset` on <html>; PageShell pads its header by that much,
 * which keeps a page's own header actions from sliding under the cluster. The
 * variable is removed when the bar unmounts (e.g. while the AI chat panel is
 * open), so the header gets its full width back.
 */
export function HeaderControlsBar({
  children,
  testId,
}: {
  children: React.ReactNode
  testId?: string
}) {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    // end-4 (1rem) of offset + 0.5rem of breathing room beside the header actions.
    const publish = () =>
      root.style.setProperty(
        "--header-controls-inset",
        `calc(${Math.ceil(el.getBoundingClientRect().width)}px + 1.5rem)`,
      )
    publish()
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish)
    observer?.observe(el)
    return () => {
      observer?.disconnect()
      root.style.removeProperty("--header-controls-inset")
    }
  }, [])

  return (
    // Anchored to the inline END: top-right left-to-right, top-left in Arabic,
    // where the sidebar moves to the right and would sit under a right-pinned bar.
    <div
      ref={ref}
      className="fixed top-3 end-4 z-30 flex items-center gap-2 pointer-events-none [&>*]:pointer-events-auto"
      data-testid={testId}
    >
      {children}
    </div>
  )
}
